/* ==========================================================================
   msk-cart.js — "The Vessel" · MSK cart drawer engine  v1.0.0
   --------------------------------------------------------------------------
   Pairs with snippets/msk-cart-drawer.liquid. Owns:
     · intercepting every add-to-cart form + every cart-icon/link click so
       the drawer opens instead of navigating to /cart (no standalone page
       in the flow; /cart survives as the no-JS fallback)
     · the Vein progress track (Carriage / Relic / The Quarter)
     · auto-adding + auto-releasing the free digital relic at its tier
       (the 100%-off is a real automatic discount; the item still has to
       be IN the cart for it to fire, so the drawer puts it there)
     · line qty/remove, the single digital upsell, rotating Codex verse
   Currency: formatted from the CART's currency, never shop.currency
   (the LF Markets lesson). USD renders bare "$59.99".
   Brand law: bone + oxblood, no glow. Reduced motion honored by CSS.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-msk-vsl]');
  if (!root) return;

  var scrim = document.querySelector('[data-msk-vsl-scrim]');
  var body = root.querySelector('[data-vsl-body]');
  var foot = root.querySelector('[data-vsl-foot]');
  var veinWrap = root.querySelector('[data-vsl-vein]');
  var fill = root.querySelector('[data-vsl-fill]');
  var voice = root.querySelector('[data-vsl-voice]');
  var countEl = root.querySelector('[data-vsl-count]');
  var subtotalEl = root.querySelector('[data-vsl-subtotal]');
  var altarRow = root.querySelector('[data-vsl-altar-row]');
  var altarEl = root.querySelector('[data-vsl-altar]');
  var verseWrap = root.querySelector('[data-vsl-verse-wrap]');
  var verseEl = root.querySelector('[data-vsl-verse]');

  /* Thresholds are authored in SHOP currency (USD) because that is how the
     automatic discounts and the free-shipping rate are denominated — Shopify
     converts the buyer's cart back to USD to evaluate them. But /cart.js
     reports totals in the PRESENTMENT currency, so the tiers must be scaled
     by Shopify.currency.rate or the bar unlocks at the wrong moment for every
     non-USD buyer (a CAD cart hit "free shipping" at ~71 real USD). */
  var fxRate = parseFloat((window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || 1);
  if (!isFinite(fxRate) || fxRate <= 0) fxRate = 1;
  function tier(attr, fallback) {
    return Math.round((parseInt(root.getAttribute(attr), 10) || fallback) * fxRate);
  }
  var SHIP = tier('data-ship-cents', 10000);
  var RELIC = tier('data-relic-cents', 20000);
  var QUARTER = tier('data-quarter-cents', 50000);
  var RELIC_VARIANT = parseInt(root.getAttribute('data-relic-variant'), 10) || 0;
  var RELIC_PRODUCT = parseInt(root.getAttribute('data-relic-product-id'), 10) || 0;

  var state = null;
  var busy = false;
  var relicWorking = false;

  /* ---------------------------------------------------------------- money */
  var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
  function money(cents) {
    var amount = (cents || 0) / 100;
    if (currency === 'USD') {
      return '$' + amount.toFixed(2);
    }
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency }).format(amount);
    } catch (e) {
      return '$' + amount.toFixed(2);
    }
  }

  /* ---------------------------------------------------------------- open/close */
  var lastFocus = null;
  function open() {
    lastFocus = document.activeElement;
    /* the Vessel outranks the email capture — never let both compete.
       Dismiss an already-open vein popup too, not just the pending state. */
    document.documentElement.classList.remove('msk-vein-pop-on');
    var veinPop = document.getElementById('msk-vein-pop');
    if (veinPop) veinPop.style.display = 'none';
    root.classList.add('is-open');
    if (scrim) scrim.classList.add('is-open');
    document.documentElement.style.overflow = 'hidden';
    root.focus();
    startVerses();
  }
  function close() {
    root.classList.remove('is-open');
    if (scrim) scrim.classList.remove('is-open');
    document.documentElement.style.overflow = '';
    stopVerses();
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }
  root.querySelectorAll('[data-vsl-close], [data-vsl-close-2]').forEach(function (b) {
    b.addEventListener('click', close);
  });
  if (scrim) scrim.addEventListener('click', close);
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && root.classList.contains('is-open')) close();
  });

  /* ---------------------------------------------------------------- verses */
  var verses = (verseEl && verseEl.getAttribute('data-verses') || '').split('||').filter(Boolean);
  var verseTimer = null, verseIdx = 0;
  function showVerse() {
    if (!verseEl || !verses.length) return;
    verseEl.style.opacity = '0';
    setTimeout(function () {
      verseEl.textContent = verses[verseIdx % verses.length];
      verseIdx++;
      verseEl.style.opacity = '1';
    }, 600);
  }
  function startVerses() {
    if (verseTimer || !verses.length) return;
    if (verseEl && !verseEl.textContent.trim()) {
      verseEl.textContent = verses[verseIdx % verses.length];
      verseIdx++;
      verseEl.style.opacity = '1';
    }
    verseTimer = setInterval(showVerse, 7000);
  }
  function stopVerses() { clearInterval(verseTimer); verseTimer = null; }

  /* ---------------------------------------------------------------- cart api */
  function getCart() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then(function (r) { return r.json(); });
  }
  function changeLine(key, qty) {
    if (busy) return Promise.resolve();
    busy = true;
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty })
    }).then(function (r) { return r.json(); }).then(function (cart) {
      busy = false;
      render(cart);
      return cart;
    }).catch(function () { busy = false; });
  }
  function addVariant(id, qty) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: id, quantity: qty || 1 }] })
    }).then(function (r) { return r.json(); });
  }

  /* ------------------------------------------------------- the free relic */
  /* the 100%-off is a real automatic discount; the drawer only makes sure
     the relic is present (or gone) at the right moment. */
  function relicLine(cart) {
    for (var i = 0; i < cart.items.length; i++) {
      if (cart.items[i].product_id === RELIC_PRODUCT) return cart.items[i];
    }
    return null;
  }
  function baseSubtotal(cart) {
    /* subtotal excluding the relic, so the tier can't be gamed by the gift */
    var line = relicLine(cart);
    return cart.items_subtotal_price - (line ? line.final_line_price : 0);
  }
  function reconcileRelic(cart) {
    if (!RELIC_VARIANT || relicWorking) return Promise.resolve(cart);
    var line = relicLine(cart);
    var earned = baseSubtotal(cart) >= RELIC;
    if (earned && !line) {
      relicWorking = true;
      return addVariant(RELIC_VARIANT, 1)
        .then(getCart)
        .then(function (c) { relicWorking = false; return c; })
        .catch(function () { relicWorking = false; return cart; });
    }
    /* it was a gift; if they fall below the tier it leaves with the tier */
    if (!earned && line && line.quantity >= 1 && cart.items.length > 1) {
      relicWorking = true;
      return fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: line.key, quantity: 0 })
      }).then(function (r) { return r.json(); })
        .then(function (c) { relicWorking = false; return c; })
        .catch(function () { relicWorking = false; return cart; });
    }
    return Promise.resolve(cart);
  }

  /* ---------------------------------------------------------------- vein */
  function renderVein(cart) {
    var base = baseSubtotal(cart);
    if (!cart.item_count) { veinWrap.hidden = true; return; }
    veinWrap.hidden = false;

    /* nodes sit at 20% / 40% / 100% of the track; map cents onto that scale */
    var pct;
    if (base <= SHIP) pct = (base / SHIP) * 20;
    else if (base <= RELIC) pct = 20 + ((base - SHIP) / (RELIC - SHIP)) * 20;
    else pct = 40 + Math.min((base - RELIC) / (QUARTER - RELIC), 1) * 60;
    fill.style.width = Math.max(0, Math.min(pct, 100)) + '%';

    [['ship', SHIP], ['relic', RELIC], ['quarter', QUARTER]].forEach(function (t) {
      var lit = base >= t[1];
      var node = root.querySelector('[data-vsl-node="' + t[0] + '"]');
      var tag = root.querySelector('[data-vsl-tag="' + t[0] + '"]');
      if (node) node.classList.toggle('is-lit', lit);
      if (tag) tag.classList.toggle('is-lit', lit);
    });

    /* his voice, never a coupon shout */
    var msg;
    if (base >= QUARTER) {
      msg = 'A quarter of the debt is forgiven. The vein keeps what it likes.';
    } else if (base >= RELIC) {
      msg = money(QUARTER - base) + ' more and a quarter of the debt is forgiven.';
    } else if (base >= SHIP) {
      msg = money(RELIC - base) + ' more and a relic comes with you — no charge.';
    } else {
      msg = money(SHIP - base) + ' more and the carriage costs you nothing.';
    }
    voice.textContent = msg;
  }

  /* ---------------------------------------------------------------- upsell */
  function upsellHTML(cart) {
    if (!RELIC_VARIANT || relicLine(cart)) return '';
    if (baseSubtotal(cart) >= RELIC) return '';
    var img = root.getAttribute('data-relic-img') || '';
    return '' +
      '<div class="msk-vsl__upsell">' +
        '<p class="msk-vsl__upsell-head">Also in the vein</p>' +
        (img ? '<img src="' + img + '" alt="">' : '<span></span>') +
        '<span>' +
          '<span class="msk-vsl__upsell-name">' + (root.getAttribute('data-relic-title') || 'Digital Relic') + '</span>' +
          '<span class="msk-vsl__upsell-price">' + (root.getAttribute('data-relic-price') || '') + ' · delivered, not shipped</span>' +
        '</span>' +
        '<button type="button" class="msk-vsl__upsell-add" data-vsl-upsell>Take it</button>' +
      '</div>';
  }

  /* ---------------------------------------------------------------- render */
  function render(cart) {
    state = cart;
    countEl.textContent = cart.item_count ? '· ' + cart.item_count + (cart.item_count === 1 ? ' held' : ' held') : '';

    if (!cart.item_count) {
      body.innerHTML =
        '<div class="msk-vsl__empty">' +
          '<div class="msk-vsl__empty-ring" aria-hidden="true"></div>' +
          '<p class="msk-vsl__empty-line">Nothing follows you yet.</p>' +
          '<a class="msk-vsl__empty-cta" href="/collections/the-dark-archetypes-by-mr-serial-killer">Enter the Reliquary</a>' +
        '</div>';
      foot.hidden = true;
      veinWrap.hidden = true;
      verseWrap.hidden = true;
      return;
    }

    foot.hidden = false;
    verseWrap.hidden = false;

    var html = '';
    cart.items.forEach(function (item) {
      var isRelic = item.product_id === RELIC_PRODUCT;
      var free = item.final_line_price === 0;
      html += '' +
        '<div class="msk-vsl__line" data-key="' + item.key + '">' +
          '<img class="msk-vsl__line-img" src="' + (item.image ? item.image.replace(/(\.[a-z]+)(\?|$)/i, '_200x$1$2') : '') + '" alt="">' +
          '<span>' +
            '<a class="msk-vsl__line-name" href="' + item.url + '">' + item.product_title + '</a>' +
            (item.variant_title && item.variant_title !== 'Default Title' ? '<span class="msk-vsl__line-var">' + item.variant_title + '</span>' : '') +
            (free ? '<span class="msk-vsl__line-free">The vein provides</span>' : '') +
            '<span class="msk-vsl__qty">' +
              '<button type="button" data-vsl-minus aria-label="Fewer">−</button>' +
              '<span>' + item.quantity + '</span>' +
              '<button type="button" data-vsl-plus aria-label="More">+</button>' +
            '</span>' +
            '<button type="button" class="msk-vsl__release" data-vsl-remove>Release it</button>' +
          '</span>' +
          '<span class="msk-vsl__line-price">' +
            (free
              ? '<s>' + money(item.original_line_price) + '</s>Free'
              : (item.final_line_price < item.original_line_price
                  ? '<s>' + money(item.original_line_price) + '</s>' + money(item.final_line_price)
                  : money(item.final_line_price))) +
          '</span>' +
        '</div>';
    });

    html += upsellHTML(cart);
    body.innerHTML = html;

    subtotalEl.textContent = money(cart.items_subtotal_price);
    var cartDiscount = 0;
    (cart.cart_level_discount_applications || []).forEach(function (a) {
      cartDiscount += a.total_allocated_amount || 0;
    });
    if (cartDiscount > 0) {
      altarRow.hidden = false;
      altarEl.textContent = '− ' + money(cartDiscount);
    } else {
      altarRow.hidden = true;
    }

    renderVein(cart);
  }

  /* ---------------------------------------------------------------- events */
  body.addEventListener('click', function (e) {
    var line = e.target.closest('.msk-vsl__line');
    if (line) {
      var key = line.getAttribute('data-key');
      var item = state && state.items.filter(function (i) { return i.key === key; })[0];
      if (!item) return;
      if (e.target.hasAttribute('data-vsl-plus')) return void changeLine(key, item.quantity + 1);
      if (e.target.hasAttribute('data-vsl-minus')) return void changeLine(key, Math.max(item.quantity - 1, 0));
      if (e.target.hasAttribute('data-vsl-remove')) return void changeLine(key, 0);
    }
    if (e.target.hasAttribute('data-vsl-upsell')) {
      e.target.textContent = '…';
      addVariant(RELIC_VARIANT, 1).then(getCart).then(render);
    }
  });

  root.querySelector('[data-vsl-checkout]').addEventListener('click', function () {
    window.location.href = '/checkout';
  });

  /* every add-to-cart form opens the Vessel instead of navigating */
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.action || form.action.indexOf('/cart/add') === -1) return;
    e.preventDefault();
    var btn = form.querySelector('[type="submit"]');
    var label = btn ? btn.textContent : '';
    if (btn) { btn.disabled = true; btn.textContent = 'Taking…'; }
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Accept': 'application/json' },
      body: new FormData(form)
    }).then(function (r) { return r.json(); })
      .then(getCart)
      .then(reconcileRelic)
      .then(function (cart) {
        render(cart);
        open();
        if (btn) { btn.disabled = false; btn.textContent = label; }
      })
      .catch(function () {
        if (btn) { btn.disabled = false; btn.textContent = label; }
        form.submit();
      });
  }, true);

  /* cart icons/links open the Vessel rather than the standalone page */
  document.addEventListener('click', function (e) {
    var a = e.target.closest('a[href$="/cart"], a[href*="/cart?"], #cart-icon-bubble, [data-msk-open-cart]');
    if (!a) return;
    e.preventDefault();
    getCart().then(reconcileRelic).then(function (cart) { render(cart); open(); });
  }, true);

  /* boot */
  getCart().then(function (cart) {
    render(cart);
  });
})();
